import { get } from 'lodash';
import styled from '@emotion/styled';
import React, { useState, useContext, useMemo, useEffect } from 'react';
/* eslint-disable no-restricted-imports */
import {
  FormGroup,
  ControlLabel,
  FormControl,
  HelpBlock,
} from 'react-bootstrap';
/* eslint-enable no-restricted-imports */

import Modal from '~/App/Components/Modal';
import ColorPickerPopover from './ColorPickerPopover';
import prependProtocol from '~/utils/prepend-protocol';
import EditorContext, { EditorContextProps } from '../Pages/Editor/Context';
import { isValidUrl } from '~/Share/Utils';
import * as URI from 'uri-js';
import { isEmail } from '~/utils/String';

const ModalHeader = styled.h3``;

interface CtaUrlValidation {
  status?: 'error' | 'success' | 'warning';
  message?: string;
}

const httpSchemes = ['http', 'https'];

const otherUriSchemes = ['mailto', 'tel'];

const knownUriSchemes = [...httpSchemes, ...otherUriSchemes];

function validateUri(value: string): {
  components: URI.URIComponents;
  valid: boolean;
} {
  const components = URI.parse(value);

  if (httpSchemes.includes(components.scheme!)) {
    return {
      components,
      valid: isValidUrl(value),
    };
  }

  if (components.scheme === 'mailto') {
    return {
      components,
      valid:
        ('to' in components &&
          components.to.length &&
          components.to.reduce<boolean>(
            (valid, email) => valid && isEmail(email),
            true
          )) ||
        false,
    };
  }

  return {
    components,
    valid: true,
  };
}

export const validators = {
  ctaText(value?: string) {
    if (value == null) {
      return;
    }

    if (value.length === 0 || value.length > 32) {
      return 'error';
    }

    return 'success';
  },

  ctaUri(value?: string): CtaUrlValidation | undefined {
    if (value == null) {
      return;
    }

    const uri = validateUri(value);

    if (uri.valid) {
      if (
        uri.components.scheme &&
        !knownUriSchemes.includes(uri.components.scheme)
      ) {
        return {
          status: 'warning',
          message: `Unknown scheme: ${uri.components.scheme}. Your Call To Action URL may not work`,
        };
      }

      return {
        status: 'success',
      };
    }

    if (value.length === 0) {
      return {
        status: 'error',
        message: 'URL is required',
      };
    }

    return {
      status: 'error',
      message: 'Please enter a valid URL',
    };
  },
};

interface CtaValidationSummary {
  valid: boolean;
  states: {
    text?: 'error' | 'success' | 'warning';
    uri?: CtaUrlValidation;
  };
}

interface CtaModalProps {
  isOpen: boolean;
  setOpen: (val: boolean) => void;
}

export default function CtaModal({ isOpen, setOpen }: CtaModalProps) {
  const context: EditorContextProps = useContext(EditorContext);
  const [textVal, setCtaText] = useState<string>();
  const [uriVal, setCtaUri] = useState<string>();
  const [isColorPickerOpen, setColorPickerOpen] = useState(false);

  const uriValWithProtocol = useMemo(
    () => uriVal && prependProtocol(uriVal),
    [uriVal]
  );

  useEffect(() => {
    if (isOpen) {
      setCtaText(undefined);
      setCtaUri(undefined);
    }
  }, [isOpen]);

  const validationSummary = useMemo<CtaValidationSummary>(() => {
    const textState = validators.ctaText(textVal);
    const uriState = validators.ctaUri(uriValWithProtocol);

    return {
      states: {
        text: textState,
        uri: uriState,
      },
      valid: textState !== 'error' && uriState?.status !== 'error',
    };
  }, [textVal, uriValWithProtocol]);

  const handleCtaTextChange = (
    evt: React.FormEvent<FormControl & HTMLInputElement>
  ) => {
    const ctaTextVal = evt.currentTarget.value.trim();
    setCtaText(ctaTextVal);
  };
  const handleCtaUriChange = (
    evt: React.FormEvent<FormControl & HTMLInputElement>
  ) => {
    const ctaUriVal = evt.currentTarget.value.trim();
    setCtaUri(ctaUriVal);
  };

  const saveAndCloseCta = () => {
    const savedTextVal =
      textVal || context.get<string>(['landing_page', 'cta', 'text']);
    const savedUrlVal =
      uriValWithProtocol ??
      uriVal ??
      context.get<string>(['landing_page', 'cta', 'uri']);

    if (context.setCta) {
      context.setCta({
        text: savedTextVal,
        uri: savedUrlVal,
      });
    }

    setOpen(false);
  };

  const modalProps = {
    isOpen: isOpen,
    onRequestClose: () => setOpen(false),
    onConfirmClick: saveAndCloseCta,
    confirmButtonDisabled: !validationSummary.valid,
  };

  return (
    <Modal {...modalProps}>
      <ModalHeader>Customise call to action</ModalHeader>
      <FormGroup validationState={validationSummary.states.text}>
        <ControlLabel>Call To Action Text</ControlLabel>
        <FormControl
          maxLength={32}
          defaultValue={context.get<string>(['landing_page', 'cta', 'text'])}
          placeholder={get(window, 'Bonjoro.messageTemplate.defaultCtaText')}
          onChange={handleCtaTextChange}
        />
      </FormGroup>
      <FormGroup validationState={validationSummary.states.uri?.status}>
        <ControlLabel>Call To Action URL</ControlLabel>
        <FormControl
          defaultValue={context.get<string>(['landing_page', 'cta', 'uri'])}
          placeholder={get(window, 'Bonjoro.messageTemplate.defaultCtaUrl')}
          onChange={handleCtaUriChange}
        />
        {validationSummary.states.uri &&
          ['error', 'warning'].includes(
            validationSummary.states.uri.status
          ) && <HelpBlock>{validationSummary.states.uri.message}</HelpBlock>}
      </FormGroup>
      <ColorPickerPopover
        isOpen={isColorPickerOpen}
        setOpen={setColorPickerOpen}
      />
    </Modal>
  );
}
